import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useCmsImages, useCmsContent } from '../context/CmsImagesContext';

export default function Services() {
  const { t } = useLanguage();
  const { getImage, getText, services, tjenesterServices } = useCmsContent();

  const activeServices = tjenesterServices && tjenesterServices.length > 0 ? tjenesterServices : services;

  const serviceImageKeyMap = {
    privatrengoring: 'service_private',
    erhvervsrengoring: 'service_business',
    flytterengoring: 'service_move'
  };

  const formatServiceDetails = (rawDetails) => {
    const localized = t(rawDetails);
    let items = [];

    if (Array.isArray(localized)) {
      items = localized;
    } else if (typeof localized === 'string' && localized.trim()) {
      const clean = localized
        .replace(/<\/?(li|p|br|div)[^>]*>/gi, '\n')
        .replace(/<[^>]+>/g, '');
      items = clean.split(/[\n\r•·]+/g);
    } else if (Array.isArray(rawDetails)) {
      items = rawDetails;
    }

    const cleanItems = items
      .map((it) => (typeof it === 'string' ? it : t(it)))
      .map((it) => {
        if (typeof it !== 'string') return '';
        return it
          .replace(/<[^>]+>/g, '')
          .replace(/^[0-9]+[\.\)]\s*/, '')
          .replace(/^[-*•·]\s*/, '')
          .trim();
      })
      .filter((it) => Boolean(it) && it.length > 1);

    if (cleanItems.length > 0) return cleanItems;

    return [
      t({ da: 'Kvalitetsrengøring med fokus på detaljen', en: 'Quality cleaning with focus on detail' }),
      t({ da: 'Miljøvenlige rengøringsmidler', en: 'Eco-friendly cleaning products' }),
      t({ da: 'Erfarne og forsikrede medarbejdere', en: 'Experienced and insured employees' })
    ];
  };

  useEffect(() => {
    document.title = t({ da: "FTR Service ApS - Tjenester", en: "FTR Service ApS - Services" });
  }, [t]);

  return (
    <Layout>
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={getImage('services_hero', '/images/hero-cleaning.png', { width: 1400 })} 
            alt="" 
            className="w-full h-full object-cover opacity-30" 
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide uppercase bg-brand-primary/10 text-brand-primary mb-6">
              {getText('tjenester_hero', 'underoverskrift', t({ da: "Vores Tjenester", en: "Our Services" }))}
            </span>
            <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              {getText('tjenester_hero', 'overskrift', t({ da: "Kvalitetsrengøring tilpasset dine behov", en: "Quality cleaning tailored to your needs" }))}
            </h1>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed">
              {getText('tjenester_hero', 'tekst', t({ 
                da: "Vi tilbyder en bred vifte af rengøringsløsninger tilpasset både private og erhverv. Vores mål er altid at levere et fejlfrit resultat, der skaber glæde og overskud i din hverdag.",
                en: "We offer a wide range of cleaning solutions adapted to both private and business. Our goal is always to deliver a flawless result that creates joy and surplus in your everyday life."
              }))}
            </p>
            <div className="flex gap-4">
              <Link to={getText('tjenester_hero', 'knap_link', '/kontakt')} className="bg-[#85d7f9] text-white px-10 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-xl shadow-[#85d7f9]/20">
                {getText('tjenester_hero', 'knap_tekst', t({ da: "Få et uforpligtende tilbud", en: "Get a non-binding quote" }))}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-20">
            {activeServices.map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`}
              >
                <div className="flex-1 relative">
                  <div className={`absolute -bottom-6 ${idx % 2 === 0 ? '-right-6' : '-left-6'} w-48 h-48 bg-[#85d7f9]/20 rounded-3xl -z-0`}></div>
                  
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl group z-10">
                    <img
                      src={getImage(serviceImageKeyMap[service.id] || 'service_private', service.image, { width: 800 })}
                      alt={t(service.title)}
                      className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </div>
                <div className="flex-1 space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900">{t(service.title)}</h2>
                  {service.description && (
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {typeof service.description === 'string' ? service.description : t(service.description)}
                    </p>
                  )}
                  
                  <ul className="space-y-4">
                    {formatServiceDetails(service.details).map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-700">
                        <CheckCircle2 className="w-5 h-5 text-brand-primary shrink-0" />
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4 flex gap-4">
                    <Link
                      to={service.orderLink || "/kontakt"}
                      className="bg-[#85d7f9] text-white px-8 py-3 rounded-xl font-bold hover:opacity-90 transition-all shadow-md shadow-[#85d7f9]/20"
                    >
                      {t(service.orderText || { da: "Bestil nu", en: "Order now" })}
                    </Link>
                    {service.link && service.link !== '/tjenester' && (
                      <Link
                        to={service.link}
                        className="border border-gray-200 text-gray-900 px-8 py-3 rounded-xl font-bold hover:border-[#85d7f9] hover:text-[#85d7f9] transition-all"
                      >
                        {t(service.readMoreText || { da: "Læs mere", en: "Read more" })}
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty Text */}
      <section className="py-24 bg-brand-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">
            {getText('tjenester_specielle_oensker', 'overskrift', t({ da: "Har du specielle ønsker?", en: "Do you have special wishes?" }))}
          </h2>
          <p className="text-blue-100 text-lg mb-10 leading-relaxed">
            {getText('tjenester_specielle_oensker', 'tekst', t({ 
              da: "Vi ved, at ingen to opgaver er ens. Derfor tilbyder vi også skræddersyede pakker, hvor vi gennemgår præcis hvad du ønsker fokus på. Kontakt os for en dialog om dine behov.",
              en: "We know that no two tasks are the same. Therefore, we also offer tailored packages where we go through exactly what you want to focus on. Contact us for a dialogue about your needs."
            }))}
          </p>
          <Link
            to={getText('tjenester_specielle_oensker', 'knap_link', '/kontakt')}
            className="inline-flex items-center gap-2 bg-white text-brand-primary px-10 py-4 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-all shadow-xl"
          >
            {getText('tjenester_specielle_oensker', 'knap_tekst', t({ da: "Få et skræddersyet tilbud", en: "Get a tailored quote" }))} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
